/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useQuery, type QueryHookOptions } from '@apollo/client';
import dayjs from 'dayjs';

import {
  convertRawDataToPlan,
  getIsPlanInaccessible,
} from '@fai/friendli-suite/features/products/container/plan';
import {
  TeamContainerPlanDocument,
  type TeamContainerPlanQuery,
  type TeamContainerPlanQueryVariables,
} from '@fai/friendli-suite/generated/client/graphql';

const ONE_DAY = 24 * 60 * 60 * 1000;

export const useContainerTeamPlan = (
  teamId: string,
  queryOptions?: QueryHookOptions<TeamContainerPlanQuery, TeamContainerPlanQueryVariables>
) => {
  const {
    data: rawData,
    loading,
    error,
  } = useQuery(TeamContainerPlanDocument, {
    variables: { teamId },
    fetchPolicy: 'network-only',
    ...queryOptions,
  });

  const userTeamData = rawData?.clientSession?.user.team;
  const productContainerData = userTeamData?.team.containerSubplan;

  const teamRole = userTeamData?.role;
  const isProductAccessible = userTeamData?.productAccess?.container;
  const productPlan = productContainerData?.tier;
  const productState = productContainerData?.state;
  const productExpiresAt = productContainerData?.expiresAt;

  const plan = convertRawDataToPlan({
    teamRole,
    isProductAccessible,
    productPlan,
    productState,
  });

  const daysLeft = productExpiresAt
    ? Math.floor(dayjs(productExpiresAt).diff() / ONE_DAY)
    : Number.NaN;

  const isPlanInaccessible = getIsPlanInaccessible(plan);

  return {
    plan,
    isPlanInaccessible,
    daysLeft,
    loading,
    error,
  };
};
